import { Injectable } from '@angular/core';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { Router } from '@angular/router';

import { AuthenticationService } from '../modules/authentication/services/authentication.service';
import { InactiveSessionsDialogComponent } from '../modules/inactive-sessions/components/inactive-sessions-dialog.component';
import { ThemesService } from './themes.service';

export const closeModalEvent = 'timeOut';
export const sessionExpirationTimeKey = 'SESSION-EXPIRATION-TIME';

@Injectable({
  providedIn: 'root',
})
export class AutoLogoutService {
  private intervalId: any;
  private showModal = false;
  private ref: MatDialogRef<InactiveSessionsDialogComponent, any>;
  private checkTime = 2000; // 2 second
  private readonly skipRoutesForSessionTracking = [
    '/login',
    '/signup',
    '/invite-signup',
    '/invite-agent',
    '/request-to-join-brokerage',
  ];

  constructor(
    private dialog: MatDialog,
    private themesService: ThemesService,
    private authenticationService: AuthenticationService,
    private router: Router,
  ) {
    this.startSessionTimer();
  }

  private startSessionTimer(): void {
    this.stopSessionTimer();

    this.intervalId = setInterval(() => this.logSessionTime(), this.checkTime);
  }

  private stopSessionTimer(): void {
    if (this.intervalId) {
      clearInterval(this.intervalId);
      this.intervalId = null;
    }
  }

  private logSessionTime(): void {
    if (this.shouldSkipSessionTracking(this.router.url)) {
      return;
    }

    const expirationTime = this.getSessionExpirationTime();
    if (!expirationTime) {
      return;
    }

    const remainingTime = expirationTime - Math.floor(Date.now() / 1000);

    if (remainingTime <= 0) {
      if (this.ref) {
        this.ref.close(closeModalEvent);
      }
      this.stopSessionTimer();
      return;
    }

    if (remainingTime < 60 && !this.showModal) {
      this.openInactiveModal();
      this.showModal = true;
      return;
    }

    if (remainingTime > 60 && this.ref) {
      this.ref.close();
    }
  }

  private shouldSkipSessionTracking(url: string): boolean {
    const path = url.split(/[?#]/)[0];
    return this.skipRoutesForSessionTracking.some((route) =>
      path.startsWith(route),
    );
  }

  private getSessionExpirationTime(): number {
    try {
      const expirationTimeValue = localStorage.getItem(
        sessionExpirationTimeKey,
      );
      if (!expirationTimeValue) {
        return 0;
      }

      return Number(expirationTimeValue) || 0;
    } catch {
      this.stopSessionTimer();
      return 0;
    }
  }

  private openInactiveModal(): void {
    this.ref = this.dialog.open(InactiveSessionsDialogComponent, {
      width: 'min(680px, calc(100vw - 32px))',
      maxWidth: 'calc(100vw - 32px)',
      maxHeight: 'calc(100vh - 48px)',
      panelClass: [this.themesService.getDarkThemeClass(), 'auto-logout'],
    });

    this.ref.afterClosed().subscribe(async (res) => {
      this.showModal = false;
      if (res === closeModalEvent) {
        await this.authenticationService.logout().toPromise();
        this.dialog.closeAll();
        return;
      }
      await this.authenticationService.getProfile().toPromise();
    });
  }
}
